import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { AlertCircle, Calendar as CalendarIcon, ChevronDown, ChevronRight, Download, RefreshCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatCurrency } from '@/lib/utils';
import { toCSV, downloadCSV } from '@/lib/csv';
import type { AgingReport, AgingRow, CustomerInvoice } from '../api';
import InvoiceRows from '../components/InvoiceRows';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import type { TriageRow } from '../types/triage';
import { computeFilteredTotals } from '../utils/agingTotals';
import { EmptyState } from '@/shared/components/EmptyState';

export interface AgingPageDrilldown {
  enabled: true;
  /**
   * Optional override for what happens when an invoice row is clicked.
   * Default = open the lightweight `InvoiceDetailDialog` (used by AP).
   * AR overrides this to navigate to `/acc/invoices/<docNumber>` so the user
   * lands on the full 3-way comparison sheet (Mongo / V4 / QBO) — same edit
   * UX as the dedicated /acc/invoices page.
   */
  onInvoiceClick?: (invoice: CustomerInvoice) => void;
}

interface AgingPageProps {
  title: string;
  description: string;
  queryKey: string;
  fetchFn: (asOfDate?: string) => Promise<AgingReport>;
  drilldown?: AgingPageDrilldown;
  /**
   * Extra buttons rendered next to the built-in QB Refresh button. AR uses
   * this to add a "Run pipeline" button that triggers acc-ar-process.
   */
  extraHeaderActions?: React.ReactNode;
  /** Per-invoice triage state from the latest acc-ar-process run. Passed to InvoiceRows so each invoice gets its action badge / signal icons. */
  triageByInvoice?: Map<string, TriageRow>;
  /** Prefix for the exported CSV filename: `<csvFilePrefix>-<asOfDate>.csv`. Defaults to 'aging'. */
  csvFilePrefix?: string;
}

type SortKey = 'name' | 'current' | 'days1to30' | 'days31to60' | 'days61to90' | 'days91plus' | 'total';

// Persist a state value to localStorage under a queryKey-scoped key.
function usePersistedState<T>(scopeKey: string, field: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const key = `atek-aging-${scopeKey}-${field}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  }, [key, value]);
  return [value, setValue];
}

export default function AgingPage({ title, description, queryKey, fetchFn, drilldown, extraHeaderActions, triageByInvoice, csvFilePrefix = 'aging' }: AgingPageProps) {
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [asOfDate, setAsOfDate] = usePersistedState(queryKey, 'asOfDate', todayStr());
  const [nameFilter, setNameFilter] = usePersistedState(queryKey, 'name', '');
  const [minCurrent, setMinCurrent] = usePersistedState(queryKey, 'minCurrent', '');
  const [min1to30, setMin1to30] = usePersistedState(queryKey, 'min1to30', '');
  const [min31to60, setMin31to60] = usePersistedState(queryKey, 'min31to60', '');
  const [min61to90, setMin61to90] = usePersistedState(queryKey, 'min61to90', '');
  const [min91plus, setMin91plus] = usePersistedState(queryKey, 'min91plus', '');
  const [minTotal, setMinTotal] = usePersistedState(queryKey, 'minTotal', '');
  const [sortKey, setSortKey] = usePersistedState<SortKey>(queryKey, 'sortKey', 'total');
  const [sortDir, setSortDir] = usePersistedState<1 | -1>(queryKey, 'sortDir', -1);
  const [hideZero, setHideZero] = usePersistedState<boolean>(queryKey, 'hideZero', true);
  const [expandedNamesArr, setExpandedNamesArr] = usePersistedState<string[]>(queryKey, 'expanded', []);
  const expandedNames = useMemo(() => new Set(expandedNamesArr), [expandedNamesArr]);
  const setExpandedNames = (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    setExpandedNamesArr((prev) => {
      const prevSet = new Set(prev);
      const next = typeof updater === 'function' ? updater(prevSet) : updater;
      return Array.from(next);
    });
  };
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);

  // Resolve the invoice-click handler once. If the page passes a custom
  // override (AR → navigate to comparison-sheet route), use that. Otherwise
  // fall back to opening the local dialog (AP behavior).
  const handleInvoiceSelect = (inv: CustomerInvoice) => {
    if (drilldown?.onInvoiceClick) drilldown.onInvoiceClick(inv);
    else setDetailInvoiceId(inv.id);
  };

  const toggleExpanded = (name: string) => {
    setExpandedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [queryKey, asOfDate],
    queryFn: () => fetchFn(asOfDate),
    staleTime: 1000 * 60 * 5,
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(key === 'name' ? 1 : -1); }
  };

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortDir === 1 ? ' ↑' : ' ↓') : '');

  const filtered = useMemo(() => {
    let list = data?.rows ?? [];
    const q = nameFilter.toLowerCase().trim();
    if (q) list = list.filter((r) => r.name.toLowerCase().includes(q));
    const parsedMin = (v: string) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
    const mc = parsedMin(minCurrent);
    const m1 = parsedMin(min1to30);
    const m3 = parsedMin(min31to60);
    const m6 = parsedMin(min61to90);
    const m9 = parsedMin(min91plus);
    const mt = parsedMin(minTotal);
    if (mc !== null) list = list.filter((r) => r.current >= mc);
    if (m1 !== null) list = list.filter((r) => r.days1to30 >= m1);
    if (m3 !== null) list = list.filter((r) => r.days31to60 >= m3);
    if (m6 !== null) list = list.filter((r) => r.days61to90 >= m6);
    if (m9 !== null) list = list.filter((r) => r.days91plus >= m9);
    if (mt !== null) list = list.filter((r) => r.total >= mt);
    if (hideZero) list = list.filter((r) => Math.abs(r.total) > 0.005);

    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string') return av.localeCompare(bv as string) * sortDir;
      return ((av as number) - (bv as number)) * sortDir;
    });
  }, [data, nameFilter, minCurrent, min1to30, min31to60, min61to90, min91plus, minTotal, sortKey, sortDir, hideZero]);

  // When unfiltered, filtered === data.rows so row-sum equals QB grand total (row-sum wins for consistency).
  const filteredTotals = useMemo(() => computeFilteredTotals(filtered), [filtered]);

  // M1 + M3: hasAmountFilter / isFiltered cover only MANUAL filters (name + Min$).
  // hideZero is intentionally excluded: hiding $0 rows never changes any dollar
  // total, so it must not trigger the "Filtered" indicator or the empty-state
  // "Clear filters" path. clearFilters must not reset hideZero either — that is
  // a persistent display preference, not a filter.
  const hasAmountFilter = Boolean(
    nameFilter.trim() ||
    minCurrent.trim() ||
    min1to30.trim() ||
    min31to60.trim() ||
    min61to90.trim() ||
    min91plus.trim() ||
    minTotal.trim(),
  );

  // isFiltered === hasAmountFilter (same intent; kept separate for readability at call-sites)
  const isFiltered = hasAmountFilter;

  const clearFilters = () => {
    setNameFilter('');
    setMinCurrent('');
    setMin1to30('');
    setMin31to60('');
    setMin61to90('');
    setMin91plus('');
    setMinTotal('');
    // NOTE: do NOT touch hideZero here — it is a display preference, not a filter.
  };

  const handleExportCSV = () => {
    if (!data || filtered.length === 0) return;
    const csv = toCSV<AgingRow>(filtered, [
      { header: 'Name',    value: (r) => r.name },
      { header: 'Current', value: (r) => r.current },
      { header: '1–30',    value: (r) => r.days1to30 },
      { header: '31–60',   value: (r) => r.days31to60 },
      { header: '61–90',   value: (r) => r.days61to90 },
      { header: '91+',     value: (r) => r.days91plus },
      { header: 'Total',   value: (r) => r.total },
    ]);
    downloadCSV(`${csvFilePrefix}-${asOfDate}.csv`, csv);
  };

  const asOfDateObj = useMemo(() => parseISO(asOfDate), [asOfDate]);
  const displayDate = format(asOfDateObj, 'PPP');

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{title}</h1>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                title="Refresh aging from QuickBooks"
              >
                <RefreshCcw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
                <span className="ml-1">Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={!data || filtered.length === 0}
                title="Export visible rows as CSV"
              >
                <Download className="h-4 w-4" />
                <span className="ml-1">Export CSV</span>
              </Button>
              {extraHeaderActions}
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              <span>Total outstanding as of {displayDate}</span>
              {/* M1: show "Filtered" badge only when a manual amount/name filter is active.
                  hideZero does NOT trigger this because hiding $0 rows never changes any total. */}
              {hasAmountFilter && (
                <Badge variant="secondary" className="normal-case tracking-normal text-[11px] py-0">
                  Filtered
                </Badge>
              )}
            </div>
            <div className="text-4xl font-bold tabular-nums mt-1">
              {data ? formatCurrency(filteredTotals.total) : (isLoading || error) ? '—' : formatCurrency(0)}
            </div>
            {/* M1: when filtered, show the grand total for reference */}
            {hasAmountFilter && data && (
              <div className="text-xs text-muted-foreground mt-0.5">
                of {formatCurrency(data.totals.total)} total
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">As of date</label>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer mr-2">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Hide $0
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-48 justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {displayDate}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={asOfDateObj}
                  onSelect={(date) => {
                    if (date) setAsOfDate(format(date, 'yyyy-MM-dd'));
                  }}
                  weekStartsOn={0}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAsOfDate(todayStr())}
              disabled={asOfDate === todayStr()}
              title="Set to today"
            >
              Today
            </Button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {isLoading && !data ? (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-background rounded-xl shadow-sm border p-5">
              <Skeleton className="h-3 w-16 mb-3" />
              <Skeleton className="h-8 w-24" />
            </div>
          ))}
        </div>
      ) : data ? (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          <BucketCard label="Current" value={filteredTotals.current} />
          <BucketCard label="1–30 days" value={filteredTotals.days1to30} />
          <BucketCard label="31–60 days" value={filteredTotals.days31to60} className="text-warning" />
          {/* TODO GAP-FE-010: no orange semantic token */}
          <BucketCard label="61–90 days" value={filteredTotals.days61to90} className="text-orange-600" />
          <BucketCard label="91+ days" value={filteredTotals.days91plus} className="text-destructive" />
          <BucketCard label="Total" value={filteredTotals.total} className="font-bold" />
        </div>
      ) : null}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-background">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>Name{sortArrow('name')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('current')}>Current{sortArrow('current')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('days1to30')}>1–30{sortArrow('days1to30')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('days31to60')}>31–60{sortArrow('days31to60')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('days61to90')}>61–90{sortArrow('days61to90')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('days91plus')}>91+{sortArrow('days91plus')}</TableHead>
              <TableHead className="text-right cursor-pointer" onClick={() => handleSort('total')}>Total{sortArrow('total')}</TableHead>
            </TableRow>
            <TableRow className="bg-muted/30">
              <TableHead className="py-1">
                <Input value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} placeholder="Filter…" className="h-7 text-xs" aria-label="Filter by name" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={minCurrent} onChange={(e) => setMinCurrent(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for Current" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={min1to30} onChange={(e) => setMin1to30(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for 1–30 days" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={min31to60} onChange={(e) => setMin31to60(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for 31–60 days" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={min61to90} onChange={(e) => setMin61to90(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for 61–90 days" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={min91plus} onChange={(e) => setMin91plus(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for 91+ days" />
              </TableHead>
              <TableHead className="py-1">
                <Input value={minTotal} onChange={(e) => setMinTotal(e.target.value)} placeholder="Min $" className="h-7 text-xs text-right w-20 ml-auto" aria-label="Minimum amount for Total" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8">
                  <div className="flex flex-col items-center gap-3">
                    <AlertCircle className="h-8 w-8 text-destructive" />
                    <p className="text-sm text-destructive">
                      {error instanceof Error ? error.message : 'Failed to load aging data from QuickBooks.'}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      Try again
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  {/* M3: three mutually-exclusive empty-state branches.
                      Branch A — manual filter active → "No matches" + Clear filters button.
                      Branch B — no manual filter, but hideZero is masking all $0 rows →
                                 "All rows are $0" + optional "Show $0 rows" button; NO
                                 clear-filters path (there are no manual filters to clear).
                      Branch C — genuinely no data from QB → neutral "No aging records". */}
                  {isFiltered ? (
                    <EmptyState
                      isFiltered
                      onClearFilters={clearFilters}
                      heading="No matches"
                      description="Try adjusting your filters or clearing them to see all records."
                    />
                  ) : hideZero && (data?.rows.length ?? 0) > 0 ? (
                    <EmptyState
                      isFiltered={false}
                      heading="All rows are $0"
                      description="Uncheck 'Hide $0' to show them."
                      action={{ label: 'Show $0 rows', onClick: () => setHideZero(false) }}
                    />
                  ) : (
                    <EmptyState
                      isFiltered={false}
                      heading="No aging records"
                      description="No aging records found for the selected date."
                    />
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filtered.map((row, i) => {
                  const expanded = Boolean(drilldown?.enabled && expandedNames.has(row.name));
                  return (
                    // M2: composite key prevents React from silently dropping a row when two
                    // QB sub-customers share the same display name (parent/child naming).
                    <Fragment key={`${row.name}__${i}`}>
                      <AgingRowItem
                        row={row}
                        expandable={Boolean(drilldown?.enabled)}
                        expanded={expanded}
                        onToggle={() => toggleExpanded(row.name)}
                      />
                      {expanded && (
                        <InvoiceRows
                          customerName={row.name}
                          asOfDate={asOfDate}
                          onSelectInvoice={handleInvoiceSelect}
                          triageByInvoice={triageByInvoice}
                        />
                      )}
                    </Fragment>
                  );
                })}
                {data && (
                  <TableRow className="font-bold bg-muted/50">
                    <TableCell>Total ({filtered.length})</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(filteredTotals.current)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(filteredTotals.days1to30)}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', filteredTotals.days31to60 > 0 && 'text-warning')}>{formatCurrency(filteredTotals.days31to60)}</TableCell>
                    {/* TODO GAP-FE-010: no orange semantic token */}
                    <TableCell className={cn('text-right tabular-nums', filteredTotals.days61to90 > 0 && 'text-orange-600')}>{formatCurrency(filteredTotals.days61to90)}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', filteredTotals.days91plus > 0 && 'text-destructive')}>{formatCurrency(filteredTotals.days91plus)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(filteredTotals.total)}</TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      <InvoiceDetailDialog
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
      />
    </div>
  );
}

function BucketCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div className="bg-background rounded-xl shadow-sm border p-5">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className={cn('text-2xl font-bold mt-2 tabular-nums', className)}>
        {formatCurrency(value)}
      </div>
    </div>
  );
}

function AgingRowItem({
  row,
  expandable,
  expanded,
  onToggle,
}: {
  row: AgingRow;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow className="hover:bg-muted/30">
      <TableCell>
        {expandable ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-left w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.name}`}
          >
            <span className="inline-flex text-muted-foreground shrink-0">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            {row.name}
          </button>
        ) : (
          row.name
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.current)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.days1to30)}</TableCell>
      <TableCell className={cn('text-right tabular-nums', row.days31to60 > 0 && 'text-warning')}>{formatCurrency(row.days31to60)}</TableCell>
      {/* TODO GAP-FE-010: no orange semantic token */}
      <TableCell className={cn('text-right tabular-nums', row.days61to90 > 0 && 'text-orange-600')}>{formatCurrency(row.days61to90)}</TableCell>
      <TableCell className={cn('text-right tabular-nums', row.days91plus > 0 && 'text-destructive')}>{formatCurrency(row.days91plus)}</TableCell>
      <TableCell className="text-right tabular-nums font-medium">{formatCurrency(row.total)}</TableCell>
    </TableRow>
  );
}
